import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent, setUserId, clearUserId } from '../utils/analytics';

// Get API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Types
export interface UserRole {
  id: string;
  name: string;
  display_name: string;
  access_level: string;
}

export interface BusinessUnit {
  id: string;
  name: string;
  display_name: string;
  /** e.g. "Delivery", "Practice", "Region" – used to filter BUs in utilization */
  bu_type?: string;
}

export interface Page {
  id: string;
  name: string;
  display_name: string;
  sidebar_name: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  picture?: string;
  business_units: BusinessUnit[];
  sub_business_units: BusinessUnit[];
  /** HRMS Pillar from happier_data (GET /user/me) */
  pillar?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  permissions: string[];
  pages: Page[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (googleToken: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  refreshUserInfo: () => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch user info with TanStack Query
  const {
    data: user = null,
    isLoading: isQueryLoading,
    refetch,
    isInitialLoading
  } = useQuery({
    queryKey: ['user-me', token],
    queryFn: async () => {
      if (!token) return null;
      
      const response = await fetch(`${API_BASE_URL}/user/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthFailure();
          return null;
        }
        throw new Error('Failed to fetch user info');
      }

      const userData = await response.json();
      
      // Ensure permissions and pages are arrays
      userData.permissions = userData.permissions || [];
      userData.pages = userData.pages || [];
      
      return userData as User;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes fresh
    gcTime: 10 * 60 * 1000,    // 10 minutes cache
    retry: (failureCount, error: any) => {
      // Don't retry on 401
      if (error?.status === 401) return false;
      return failureCount < 3;
    }
  });

  const handleAuthFailure = useCallback(() => {
    clearUserId();
    localStorage.removeItem('access_token');
    setToken(null);
    queryClient.setQueryData(['user-me', token], null);
    navigate('/login');
  }, [queryClient, token, navigate]);

  // Login with Google token
  const login = async (googleToken: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${googleToken}`,
        },
        body: JSON.stringify({ google_token: googleToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const { access_token } = data;

        // Save token locally and in state
        localStorage.setItem('access_token', access_token);
        setToken(access_token);
        trackEvent('login', { method: 'google' });
        // React Query will automatically trigger refetch because 'token' in queryKey changed
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      trackEvent('logout');
      clearUserId();
      localStorage.removeItem('access_token');
      setToken(null);
      queryClient.setQueryData(['user-me', token], null);
      queryClient.removeQueries({ queryKey: ['user-pages'] }); // Clear pages cache too
      navigate('/login');
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user || !user.role) return false;
    return roles.includes(user.role.name);
  };

  const refreshUserInfo = async () => {
    await refetch();
  };

  useEffect(() => {
    if (user?.email) {
      setUserId(user.email);
    }
  }, [user?.email]);

  // Final loading state: true if we have a token but haven't loaded the user yet
  const isLoading = (!!token && isQueryLoading);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    hasPermission,
    hasAnyRole,
    refreshUserInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Roles mapping
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  LEADERSHIP: 'leadership',
  BU_LEAD: 'bu_lead',
  SBU_LEAD: 'sbu_lead',
  MANAGER: 'manager',
  INDIVIDUAL: 'individual',
  PSBP: 'psbp',
  FINANCE: 'finance',
  VIEWER: 'viewer',
  EXECUTIVE: 'leadership',
  ANALYST: 'individual',
} as const;

export const PERMISSIONS = {
  DASHBOARD_READ: 'dashboard.read',
  ANALYTICS_READ: 'analytics.read',
  PEOPLE_ANALYTICS_READ: 'people_analytics.read',
  PROJECTS_READ: 'projects.read',
  FINANCE_READ: 'finance.read',
  USERS_CREATE: 'users.create',
  USERS_READ: 'users.read',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  ROLES_CREATE: 'roles.create',
  ROLES_READ: 'roles.read',
  ROLES_UPDATE: 'roles.update',
  ROLES_DELETE: 'roles.delete',
  PERMISSIONS_READ: 'permissions.read',
  PERMISSIONS_ASSIGN: 'permissions.assign',
  BUSINESS_UNITS_CREATE: 'business_units.create',
  BUSINESS_UNITS_READ: 'business_units.read',
  BUSINESS_UNITS_UPDATE: 'business_units.update',
  BUSINESS_UNITS_DELETE: 'business_units.delete',
  PAGES_CREATE: 'pages.create',
  PAGES_READ: 'pages.read',
  PAGES_UPDATE: 'pages.update',
  PAGES_DELETE: 'pages.delete',
  AUDIT_LOGS_READ: 'audit_logs.read',
  SETTINGS_READ: 'settings.read',
  SETTINGS_UPDATE: 'settings.update',
  VIEW_PROJECTS: 'projects.read',
  VIEW_PEOPLE_ANALYTICS: 'people_analytics.read',
  VIEW_BUSINESS_UNITS: 'business_units.read',
  MANAGE_USERS: 'users.create',
  MANAGE_ROLES: 'roles.create',
  MANAGE_PERMISSIONS: 'permissions.assign',
  VIEW_AUDIT_LOGS: 'audit_logs.read',
  VIEW_ALL_DATA: 'analytics.read',
  VIEW_SBU_DATA: 'business_units.read',
  VIEW_TEAM_DATA: 'analytics.read',
  VIEW_DIRECT_REPORTS: 'users.read',
  VIEW_UTILIZATION: 'analytics.read',
} as const;
